import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IconShare3,
  IconCirclePlusFilled,
  IconLifebuoy,
  IconShoppingCart,
  IconHistory,
  IconChevronRight,
  IconPillOff,
  IconSunrise,
  IconSun,
  IconSunset2,
  IconMoon,
} from "@tabler/icons-react";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import MFab from "@/components/ui/atoms/MFab";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { triggerNativePillboxShare } from "@/utils/nativeBridge";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { remindersService } from "@/services/reminders.service";
import { Reminders, ReminderType } from "@/types/reminders";
import { FilterOperation, FilterType } from "@/types/filter";
import {
  DaytimeSlot,
  DoseEvent,
  PendingChangeType,
  PillboxItem,
  PillboxPendingChange,
  ScheduleDay,
} from "@/types/pillbox";
import {
  formatFrequencyLine,
  formatMedicineTitle,
} from "./utils/formatMedicine";
import { buildReminderPrefillFromPillboxItem } from "./utils/reminderPrefill";
import WeekDateStrip from "./components/WeekDateStrip";
import DaytimeSlotRow from "./components/DaytimeSlotRow";
import PeriodSection from "./components/PeriodSection";
import MedicineCard from "./components/MedicineCard";
import StopMedicationModal from "./components/StopMedicationModal";
import styles from "./PillboxLanding.module.css";

type ViewMode = "schedule" | "list";

const PERIOD_ICONS: Record<DaytimeSlot, React.ReactNode> = {
  morning: <IconSunrise size={24} stroke={1.5} color="#45168C" />,
  afternoon: <IconSun size={24} stroke={1.5} color="#45168C" />,
  evening: <IconSunset2 size={24} stroke={1.5} color="#45168C" />,
  night: <IconMoon size={24} stroke={1.5} color="#45168C" />,
};
const PERIOD_ORDER: DaytimeSlot[] = [
  "morning",
  "afternoon",
  "evening",
  "night",
];

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const result = new Date(d);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - result.getDay());
  return result;
}

function getWeekDates(anchor: Date): Date[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function bucketDoseTime(time: string): DaytimeSlot {
  const hour = parseInt(time.split(":")[0], 10);
  if (hour < 12) return "morning";
  if (hour < 16) return "afternoon";
  if (hour < 20) return "evening";
  return "night";
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const PillboxLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  const {
    fetchLedger,
    fetchSchedule,
    fetchPending,
    stopItem,
    toggleRefill,
    downloadSharePdf,
  } = usePillbox(memberId);

  const [viewMode, setViewMode] = useState<ViewMode>("schedule");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weekSchedule, setWeekSchedule] = useState<ScheduleDay[]>([]);
  const [ledger, setLedger] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<PillboxPendingChange[]>(
    []
  );
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [stopTarget, setStopTarget] = useState<PillboxItem | null>(null);
  const [stopping, setStopping] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [highlightedPeriod, setHighlightedPeriod] =
    useState<DaytimeSlot | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [remindersBySourceId, setRemindersBySourceId] = useState<
    Map<string, Reminders>
  >(new Map());

  // Reminders set from Pillbox are linked back to their medicine via
  // sourceId (see reminderPrefill.ts) — fetch them all once so each card
  // can show its schedule inline and route to "Edit reminder" instead of
  // "Add reminder" once one exists.
  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    remindersService
      .getReminders(memberId, {
        filters: {
          type: {
            operation: FilterOperation.EQUAL,
            value: ReminderType.MEDICATION,
            type: FilterType.STRING,
          },
        },
        sort: [],
        pagination: { page: 1, pageSize: 200 },
      })
      .then((response) => {
        if (cancelled) return;
        const map = new Map<string, Reminders>();
        for (const reminder of response.data as Reminders[]) {
          if (reminder.sourceId) map.set(reminder.sourceId, reminder);
        }
        setRemindersBySourceId(map);
      })
      .catch(() => {
        if (!cancelled) setRemindersBySourceId(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const handleSelectPeriod = useCallback((period: DaytimeSlot) => {
    document
      .getElementById(`pillbox-period-${period}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedPeriod(period);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(
      () => setHighlightedPeriod(null),
      900
    );
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current)
        clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const weekStartKey = toDateKey(startOfWeek(selectedDate));
  const reloadTokenRef = useRef(0);

  const reload = useCallback(() => {
    if (!memberId) return;
    const token = ++reloadTokenRef.current;
    setLoading(true);
    Promise.all([fetchSchedule(weekStartKey, 7), fetchLedger("ACTIVE")])
      .then(([schedule, items]) => {
        if (reloadTokenRef.current !== token) return;
        setWeekSchedule(schedule);
        setLedger(items);
      })
      .catch(() => {
        if (reloadTokenRef.current !== token) return;
        setWeekSchedule([]);
        setLedger([]);
      })
      .finally(() => {
        if (reloadTokenRef.current === token) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, weekStartKey]);

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    fetchPending()
      .then((changes) => {
        if (!cancelled) setPendingChanges(changes);
      })
      .catch(() => {
        if (!cancelled) setPendingChanges([]);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, fetchPending]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, weekStartKey]);

  const pendingAddChanges = useMemo(
    () =>
      pendingChanges.filter(
        (change) => change.changeType === PendingChangeType.ADD
      ),
    [pendingChanges]
  );
  const pendingPrescriptionCount = useMemo(
    () => new Set(pendingAddChanges.map((c) => c.sourcePrescriptionId)).size,
    [pendingAddChanges]
  );

  const weekDates = getWeekDates(selectedDate);
  const selectedDayKey = toDateKey(selectedDate);
  const selectedDay = weekSchedule.find((d) => d.date === selectedDayKey);
  const doses = selectedDay?.doses ?? [];
  const conditional = selectedDay?.conditional ?? [];
  const isDayEmpty = doses.length === 0 && conditional.length === 0;

  const datesWithData = useMemo(() => {
    const set = new Set<string>();
    for (const day of weekSchedule) {
      if (day.doses.length > 0 || day.conditional.length > 0) set.add(day.date);
    }
    return set;
  }, [weekSchedule]);

  const dosesByPeriod = useMemo(() => {
    const grouped: Record<DaytimeSlot, DoseEvent[]> = {
      morning: [],
      afternoon: [],
      evening: [],
      night: [],
    };
    for (const dose of selectedDay?.doses ?? []) {
      grouped[bucketDoseTime(dose.time)].push(dose);
    }
    for (const period of PERIOD_ORDER) {
      grouped[period].sort((a, b) => a.time.localeCompare(b.time));
    }
    return grouped;
  }, [selectedDay]);

  const daytimeCounts: Record<DaytimeSlot, number> = {
    morning: dosesByPeriod.morning.length,
    afternoon: dosesByPeriod.afternoon.length,
    evening: dosesByPeriod.evening.length,
    night: dosesByPeriod.night.length,
  };

  const activeSlot = isToday(selectedDate)
    ? bucketDoseTime(`${String(new Date().getHours()).padStart(2, "0")}:00`)
    : null;

  const scheduledLedger = ledger.filter(
    (item) => item.timingRuleType !== "CONDITIONAL"
  );
  const scheduledMedsCount = scheduledLedger.length;

  // List view only shows scheduled medicines — SOS/conditional items live
  // exclusively on the SOS screen.
  const alphabeticalGroups = useMemo(() => {
    const groups = new Map<string, PillboxItem[]>();
    const sorted = [...scheduledLedger].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    for (const item of sorted) {
      const letter = item.name.charAt(0).toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(item);
    }
    return Array.from(groups.entries());
  }, [scheduledLedger]);

  const itemsById = useMemo(
    () => new Map(ledger.map((item) => [item.id, item])),
    [ledger]
  );

  const handleAdd = () => navigate("/pillbox/add-medicine");
  const handleSelectDose = (dose: DoseEvent) =>
    navigate(`/pillbox/${memberId}/${dose.itemType}/${dose.itemId}`);
  const handleSelectListItem = (item: PillboxItem) =>
    navigate(`/pillbox/${memberId}/${item.itemType}/${item.id}`);

  const handleEditDetails = (item: PillboxItem) =>
    navigate("/pillbox/add-medicine", { state: { mode: "edit", item } });
  const handleEditDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleEditDetails(item);
  };

  const handleStopRequest = (item: PillboxItem) => setStopTarget(item);
  const handleStopDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleStopRequest(item);
  };

  const handleAddReminder = (item: PillboxItem) => {
    const prefill = buildReminderPrefillFromPillboxItem(item);
    if (!prefill) return;
    navigate("/reminders/medication", { state: { pillboxPrefill: prefill } });
  };
  const getReminderPrefillForDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    return item ? buildReminderPrefillFromPillboxItem(item) : null;
  };

  const getReminderForItem = (item: PillboxItem) =>
    remindersBySourceId.get(item.id) ?? null;
  const getReminderForDose = (dose: DoseEvent) =>
    remindersBySourceId.get(dose.itemId) ?? null;
  const handleEditReminder = (reminder: Reminders) =>
    navigate("/reminders/medication", { state: { reminder } });

  const handleToggleRefill = async (item: PillboxItem) => {
    const nextNeedsRefill = !item.needsRefill;
    try {
      await toggleRefill(item.itemType, item.id, nextNeedsRefill);
      showToast({
        variant: "success",
        message: t(
          nextNeedsRefill
            ? "pillbox.shoppingList.addedToast"
            : "pillbox.shoppingList.removedToast",
          { name: formatMedicineTitle(item) }
        ),
      });
      reload();
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    }
  };
  const handleToggleRefillDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleToggleRefill(item);
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const view = viewMode === "list" ? "list" : "schedule";

      // 1. Native webview: ask the shell to fetch the PDF and present the OS
      //    share sheet — same path as vitals/lab-insights share.
      const handed = triggerNativePillboxShare({
        memberId,
        memberName: memberDisplayName,
        view,
        date: view === "schedule" ? selectedDayKey : undefined,
      });
      if (handed) return;

      // 2. Web fallback: download the PDF directly.
      const blob = await downloadSharePdf(
        view,
        view === "schedule" ? selectedDayKey : undefined
      );
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shareError"),
      });
    } finally {
      setSharing(false);
    }
  };

  const handleConfirmStop = async () => {
    if (!stopTarget) return;
    setStopping(true);
    try {
      await stopItem(stopTarget.itemType, stopTarget.id);
      showToast({
        variant: "success",
        message: t("pillbox.stopModal.successMessage", {
          name: formatMedicineTitle(stopTarget),
        }),
      });
      setStopTarget(null);
      reload();
    } finally {
      setStopping(false);
    }
  };

  const headerDateLabel = selectedDate.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <div className={styles.iconSpacer} />
          <h1 className={styles.title}>{t("pillbox.title")}</h1>
          <button
            type="button"
            className={styles.headerShareButton}
            onClick={handleShare}
            disabled={sharing}
            aria-label={t("pillbox.share")}
          >
            <IconShare3 size={24} stroke={1.5} className={styles.headerIcon} />
          </button>
        </div>
        <div className={styles.switchMemberRow}>
          <MProfilePicker />
          <button
            type="button"
            className={styles.headerAddButton}
            onClick={handleAdd}
            aria-label={t("pillbox.add")}
          >
            <IconCirclePlusFilled size={32} />
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.quickActionsRow}>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() => navigate("/pillbox/sos")}
          >
            <span className={styles.quickActionIcon}>
              <IconLifebuoy size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>{t("pillbox.sos")}</span>
          </button>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() => navigate("/pillbox/shopping-list")}
          >
            <span className={styles.quickActionIcon}>
              <IconShoppingCart size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>
              {t("pillbox.shoppingList.title")}
            </span>
          </button>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() => navigate("/pillbox/past-medications")}
          >
            <span className={styles.quickActionIcon}>
              <IconHistory size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>
              {t("pillbox.pastMedications")}
            </span>
          </button>
        </div>

        {!bannerDismissed && pendingAddChanges.length > 0 && (
          <div className={styles.pendingBanner}>
            <p className={styles.pendingBannerTitle}>
              {t("pillbox.review.bannerTitle", {
                count: pendingAddChanges.length,
                member: memberDisplayName,
              })}
            </p>
            <p className={styles.pendingBannerSubtitle}>
              {t("pillbox.review.bannerSubtitle", {
                count: pendingPrescriptionCount,
                member: memberDisplayName,
              })}
            </p>
            <div className={styles.pendingBannerActions}>
              <button
                type="button"
                className={styles.pendingBannerIgnore}
                onClick={() => setBannerDismissed(true)}
              >
                {t("pillbox.review.ignore")}
              </button>
              <button
                type="button"
                className={styles.pendingBannerReview}
                onClick={() => navigate("/pillbox/review")}
              >
                {t("pillbox.review.review")}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className={styles.loaderWrapper}>
            <MLoader size={32} />
          </div>
        ) : viewMode === "schedule" ? (
          <>
            <div className={styles.sectionHeaderRow}>
              <span className={styles.sectionHeaderTitle}>
                {headerDateLabel}
              </span>
              <button
                type="button"
                className={styles.viewToggle}
                onClick={() => setViewMode("list")}
              >
                {t("pillbox.listView")}
                <IconChevronRight size={16} />
              </button>
            </div>

            <WeekDateStrip
              weekDates={weekDates}
              selectedDate={selectedDate}
              datesWithData={datesWithData}
              onSelect={setSelectedDate}
            />

            <DaytimeSlotRow
              counts={daytimeCounts}
              activeSlot={activeSlot}
              onSelect={handleSelectPeriod}
            />

            {isDayEmpty ? (
              <MEmptyCard
                className={styles.emptyCard}
                icon={<IconPillOff size={64} stroke={1.5} color="#AD95D1" />}
                primaryTitle={t("pillbox.emptyDay.title")}
              />
            ) : (
              <div className={styles.periodSections}>
                {PERIOD_ORDER.map((period) => (
                  <PeriodSection
                    key={period}
                    id={`pillbox-period-${period}`}
                    period={period}
                    icon={PERIOD_ICONS[period]}
                    doses={dosesByPeriod[period]}
                    highlighted={highlightedPeriod === period}
                    onSelectDose={handleSelectDose}
                    onEditDose={handleEditDose}
                    onStopDose={handleStopDose}
                    getReminderPrefillForDose={getReminderPrefillForDose}
                    getReminderForDose={getReminderForDose}
                    getNeedsRefillForDose={(dose) =>
                      itemsById.get(dose.itemId)?.needsRefill ?? false
                    }
                    onToggleRefillDose={handleToggleRefillDose}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.sectionHeaderRow}>
              <span className={styles.sectionHeaderTitle}>
                {t("pillbox.scheduledMeds", { count: scheduledMedsCount })}
              </span>
              <button
                type="button"
                className={styles.viewToggle}
                onClick={() => setViewMode("schedule")}
              >
                {t("pillbox.scheduleView")}
                <IconChevronRight size={16} />
              </button>
            </div>

            {scheduledLedger.length === 0 ? (
              <MEmptyCard
                className={styles.emptyCard}
                icon={<IconPillOff size={64} stroke={1.5} color="#AD95D1" />}
                primaryTitle={t("pillbox.emptyList.title")}
              />
            ) : (
              <div className={styles.alphabeticalList}>
                {alphabeticalGroups.map(([letter, items]) => (
                  <div className={styles.alphaSection} key={letter}>
                    <div className={styles.alphaHeader}>
                      <span className={styles.alphaLetter}>{letter}</span>
                      <div className={styles.alphaDivider} />
                    </div>
                    {items.map((item) => {
                      const reminder = getReminderForItem(item);
                      return (
                        <MedicineCard
                          key={item.id}
                          itemType={item.itemType}
                          title={formatMedicineTitle(item)}
                          frequencyLine={formatFrequencyLine(
                            item,
                            item.recurrenceIntervalDays
                          )}
                          trailingIcon="dots"
                          onClick={() => handleSelectListItem(item)}
                          onEditDetails={() => handleEditDetails(item)}
                          reminder={reminder}
                          onAddReminder={
                            !reminder &&
                            buildReminderPrefillFromPillboxItem(item)
                              ? () => handleAddReminder(item)
                              : undefined
                          }
                          onEditReminder={
                            reminder
                              ? () => handleEditReminder(reminder)
                              : undefined
                          }
                          needsRefill={item.needsRefill}
                          onToggleRefill={() => handleToggleRefill(item)}
                          onStopMedication={() => handleStopRequest(item)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <MFab />

      <StopMedicationModal
        open={!!stopTarget}
        onClose={() => setStopTarget(null)}
        onConfirm={handleConfirmStop}
        loading={stopping}
      />
    </div>
  );
};

export default PillboxLanding;
